import styles from "./styles.module.scss";
import { ProductCard } from "../ProductCard";
import { useProductStore } from "src/store/overviewProduct.state";
import { Input } from "@components/ui-kit/Input";
import ModalBox from "@components/ui-kit/ModalBox";
import Search from "@assets/icons/search";
import { useState, useEffect, useRef } from "react";
import { ProductsService } from "@services/amazon/products/products.service";
import { Spin } from "antd";
import classNames from "classnames";
import { SearchList } from "./SearchList";

interface CompareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CompareModal = ({ isOpen, onClose }: CompareModalProps) => {
  const { selectedProductToCompare = [], setSelectedProductToCompare } =
    useProductStore();
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsLoaded, setTopProductsLoaded] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingTop, setLoadingTop] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const productsService = useRef(new ProductsService()).current;
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (!isOpen || topProductsLoaded) return;
    setLoadingTop(true);
    productsService
      .getOwnProducts({ limit: 5, page: 1 })
      .then((res) => {
        setTopProducts(res);
        setTopProductsLoaded(true);
      })
      .catch(() => setTopProducts([]))
      .finally(() => setLoadingTop(false));
  }, [isOpen, topProductsLoaded, productsService]);

  useEffect(() => {
    if (!isOpen || !debouncedSearch.trim()) {
      setSearchResults([]);
      return;
    }
    setLoadingSearch(true);
    productsService
      .searchOwnProducts({ asin: debouncedSearch.trim(), limit: 32 })
      .then((res) => setSearchResults(res))
      .catch(() => setSearchResults([]))
      .finally(() => setLoadingSearch(false));
  }, [isOpen, debouncedSearch, productsService]);

  const handleClose = () => {
    setSearch("");
    setDebouncedSearch("");
    setSearchResults([]);
    onClose();
  };

  const handleSelect = (item) => {
    const isSelected = selectedProductToCompare.some(
      (p) => p.asin === item.asin,
    );
    if (isSelected) {
      setSelectedProductToCompare(
        selectedProductToCompare.filter((p) => p.asin !== item.asin),
      );
    } else {
      setSelectedProductToCompare([...selectedProductToCompare, item]);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalBox isOpen={isOpen} onClose={handleClose} title={"Product Selection"}>
      <div className={styles.content}>
        <h3>Top 5 performers</h3>
        <div
          className={classNames(styles.tray, { [styles.centered]: loadingTop })}
        >
          {loadingTop ? (
            <Spin size="large" />
          ) : (
            topProducts.map((item) => (
              <ProductCard
                key={item.asin}
                item={item}
                selected={selectedProductToCompare.some(
                  (p) => p.asin === item.asin,
                )}
                onSelect={handleSelect}
              />
            ))
          )}
        </div>
        <div className={styles.actions}>
          <div className={styles.search}>
            <h3>Search products</h3>
            <Input
              placeholder="Search"
              value={search}
              icon={<Search />}
              className={styles.customInput}
              onChange={setSearch}
            />
          </div>
          <button className={styles.compareBtn} onClick={handleClose}>
            Compare
          </button>
        </div>
        {debouncedSearch.trim() && (
          <div className={styles.searchResults}>
            {loadingSearch ? (
              <Spin size="small" />
            ) : searchResults.length === 0 ? (
              <div className={styles.noResults}>No products found</div>
            ) : (
              <SearchList
                searchResults={searchResults}
                selectedProductToCompare={selectedProductToCompare}
                handleSelect={handleSelect}
              />
            )}
          </div>
        )}
      </div>
    </ModalBox>
  );
};
